import React, { useState, useRef, useEffect } from "react";
import {
  View,
  TouchableOpacity,
  Text,
  StyleSheet,
  Animated,
  Easing,
} from "react-native";
import { useTranslation } from "react-i18next";
import { useDevice } from "./../_layout";
import Colors from "../../Resources/Color";

const MultipleSegmentedPicker = ({
  options,
  value,
  onChange,
  small = false,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useDevice();
  const [hoveredIndex, setHoveredIndex] = useState(null);

  // Animation state
  const [segmentLayouts, setSegmentLayouts] = useState({});
  const indicatorX = useRef(new Animated.Value(0)).current;
  const indicatorWidth = useRef(new Animated.Value(0)).current;
  const [hasMeasurements, setHasMeasurements] = useState(false);

  const handlePress = (index) => {
    onChange(index);
  };

  // Run animation when value or layouts change
  useEffect(() => {
    const target = segmentLayouts[value];
    if (target) {
      if (!hasMeasurements) {
        // Initial placement without animation
        indicatorX.setValue(target.x);
        indicatorWidth.setValue(target.width);
        setHasMeasurements(true);
      } else {
        // Animate
        Animated.parallel([
          Animated.timing(indicatorX, {
            toValue: target.x,
            duration: 200,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: false,
          }),
          Animated.timing(indicatorWidth, {
            toValue: target.width,
            duration: 200,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: false,
          }),
        ]).start();
      }
    }
  }, [value, segmentLayouts]);

  const onLayout = (index, event) => {
    const { x, width } = event.nativeEvent.layout;
    setSegmentLayouts((prev) => {
      // Only update if changed to avoid loops
      if (prev[index]?.x === x && prev[index]?.width === width) return prev;
      return { ...prev, [index]: { x, width } };
    });
  };

  return (
    <View style={styles.containerPicker}>
      <View style={styles.segmentedControl}>
        {/* Animated Background Indicator */}
        {hasMeasurements && (
          <Animated.View
            style={[
              styles.indicator,
              {
                left: indicatorX,
                width: indicatorWidth,
              },
            ]}
          />
        )}

        {options.map((option, index) => {
          const selected = index === value;
          const hovered = hoveredIndex === index;

          return (
            <TouchableOpacity
              key={option}
              style={[
                styles.segment,
                {
                  paddingVertical: small ? 5 : 6,
                  paddingHorizontal: small ? 14 : 14,
                },
              ]}
              onPress={() => handlePress(index)}
              onLayout={(e) => onLayout(index, e)}
              onMouseEnter={() => !isMobile && setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              activeOpacity={0.8}
            >
              <Text
                style={[
                  styles.text,
                  selected && styles.textSelected,
                  !selected && hovered && styles.textHovered,
                  { fontSize: small ? 12 : 12 },
                ]}
              >
                {t(option)}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  containerPicker: {
    backgroundColor: Colors.lightGrayColor,
    borderRadius: 20,
    padding: 3,
    alignSelf: "flex-start",
  },
  segmentedControl: {
    flexDirection: "row",
    alignSelf: "flex-start",
    position: "relative", // Needed for absolute positioning context if not View
  },
  indicator: {
    position: "absolute",
    top: 0,
    bottom: 0,
    backgroundColor: Colors.whiteColor,
    borderRadius: 16,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
    marginVertical: 0, // Match segment margin if any, or adjust height
  },
  segment: {
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 16,
    marginHorizontal: 0, // Removed margin to make math easier for indicator
    zIndex: 1, // Ensure text is above indicator
  },
  text: {
    color: Colors.grayColor,
    fontWeight: "500",
  },
  textSelected: {
    color: Colors.blackColor,
    fontWeight: "600",
  },
  textHovered: {
    color: Colors.darkgrayColor,
  },
});

export default MultipleSegmentedPicker;
